import { EmissionReportStatus } from '@/api/EmissionReportModel'
import { Report } from '@/hooks/api/v2/reports'
import useI18n from '@/shared/hooks/useI18n'
import { useReportFormatting } from '@/shared/hooks/useReportFormatting'
import { Button, Card, CardBody } from '@/common'
import {
  CheckCircleIcon,
  ClockArrowIcon,
  TrashIcon
} from '@/common/components/icons'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { isWithinInterval } from 'date-fns'
import cx from 'classnames'

const EmissionItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex gap-x-2 justify-center items-center">
      <p className="flex-1 text-xs xl:text-right">{label}</p>
      <p className="flex-1 text-center text-sm">{value}</p>
    </div>
  )
}

export default function ReportListItem({
  report,
  onDelete,
  hoveredPeriod
}: {
  report: Report
  onDelete: (reportId: string, formattedReportPeriod: string) => void
  hoveredPeriod: string | null
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { checkPermission } = usePermissions()

  const isHovered = hoveredPeriod
    ? isWithinInterval(new Date(hoveredPeriod), {
        start: new Date(report.startDate),
        end: new Date(report.endDate)
      })
    : false

  const canDeleteReport = checkPermission('emissionReport', 'delete')
  const canEditReport = checkPermission('emissionReport', 'update')

  const {
    formatReportPeriod,
    formatActivityLevel,
    formatSpecificDirectEmbeddedEmissions,
    formatSpecificIndirectEmbeddedEmissions
  } = useReportFormatting()

  const startDate = new Date(report.startDate)
  const endDate = new Date(report.endDate)
  const formattedReportPeriod = formatReportPeriod(startDate, endDate)

  const formattedActivityLevelValue = formatActivityLevel(
    report.methodEmissionReports[0].activityLevel
  )

  const formattedSpecificDirectEmbeddedEmissionsValue = formatSpecificDirectEmbeddedEmissions(
    report.methodEmissionReports[0].specificDirectEmbeddedEmissions
  )

  const formattedSpecificIndirectEmbeddedEmissionsValue = formatSpecificIndirectEmbeddedEmissions(
    report.methodEmissionReports[0].specificIndirectEmbeddedEmissions
  )

  return (
    <Card
      className={cx('transition-all duration-300', {
        'ring-2 -ring-offset-2 ring-green-400': isHovered
      })}
    >
      <CardBody className="xl:grid xl:grid-cols-5 xl:gap-4 space-y-2">
        <div>
          <h4>{formattedReportPeriod}</h4>
          <p className="text-text-secondary text-sm">
            {t('reports:activityLevel')}{' '}
            <span className="text-green-600 font-semibold">{formattedActivityLevelValue}</span>
          </p>
        </div>
        <div className="lg:grid grid-cols-2 gap-2 col-span-2">
          <EmissionItem
            label={t('reports:reportListItem.specificDirectEmbeddedEmissions')}
            value={formattedSpecificDirectEmbeddedEmissionsValue}
          />
          <EmissionItem
            label={t('reports:reportListItem.specificIndirectEmbeddedEmissions')}
            value={formattedSpecificIndirectEmbeddedEmissionsValue}
          />
        </div>
        <div className="flex justify-end">
          {report.status === EmissionReportStatus.DRAFT && (
            <span className="flex items-center w-28 text-sm">
              <ClockArrowIcon className="size-5 mr-1 text-yellow-500" />
              {t('reports:reportListItem.draft')}
            </span>
          )}
          {report.status === EmissionReportStatus.SUBMITTED && (
            <span className="flex items-center w-28 text-sm">
              <CheckCircleIcon className="size-5 mr-1 text-green-600" />
              {t('reports:reportListItem.submitted')}
            </span>
          )}
        </div>
        <div className="flex justify-end gap-x-2">
          <div className="flex gap-x-2 justify-end items-center shrink-0">
            <Button
              disabled={!canEditReport}
              variant="subtle"
              sizeType="sm"
              onClick={() => navigate(`./view/${report.id}`)}
            >
              {report.status === EmissionReportStatus.DRAFT
                ? t('reports:reportListItem.editReportButton')
                : t('reports:reportListItem.viewReportButton')}
            </Button>
            <Button
              disabled={
                !canDeleteReport ||
                !canEditReport ||
                report.status === EmissionReportStatus.SUBMITTED
              }
              variant="danger"
              icon={<TrashIcon />}
              sizeType="sm"
              onClick={() => onDelete(report.id, formattedReportPeriod)}
            />
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
